import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext'; // 引入全域狀態
import { api, KeyEntry } from '../../services/api'; // 引入 API 與型別

// 💡 新增：處理時區偏移的日期格式化函式，確保發送日期顯示正確
const formatLocalYYYYMMDD = (dateString: string | null | undefined) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return String(dateString).split('T')[0];
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function KeyInfo() {
  const [keys, setKeys] = useState<KeyEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const { token } = useAuth(); // 從 Context 中取得 JWT Token

  // 組件載入時，呼叫後端 API 取得真實序號資料
  useEffect(() => {
    const fetchKeys = async () => {
      if (!token) return;
      try {
        setIsLoading(true);
        setError('');
        const data = await api.getMyKeys(token);
        
        // 確保資料映射正確，相容 snake_case 與 camelCase
        const formattedData = data.map((item: any) => ({
          ...item,
          keyDeliveryDate: item.keyDeliveryDate || item.key_delivery_date,
          fulfillmentStatus: item.fulfillmentStatus || item.fulfillment_status,
        }));
        
        setKeys(formattedData);
      } catch (err: any) {
        setError(err.message || '無法載入序號，請稍後再試');
      } finally {
        setIsLoading(false);
      }
    };

    fetchKeys();
  }, [token]);

  // 完美保留您的複製功能邏輯
  const handleCopy = async (key: string, id: number) => {
    if (!key) return; // 防呆：空序號不複製
    try {
      await navigator.clipboard.writeText(key);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = key;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  return (
    <div className="w-full px-6 md:px-10 py-12 md:py-20">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <p className="text-text-muted text-sm mb-1">通通在這！你的 Steam Key 寶庫</p>
          <h1 className="text-text-dark text-2xl md:text-3xl font-heading font-bold">遊戲序號管理</h1>
        </div>

        {/* Keys Table */}
        <div className="bg-white rounded-2xl border border-fog-mid overflow-hidden shadow-sm mb-6">
          <div className="p-6 md:p-8 border-b border-fog-mid">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-fog border border-fog-mid flex items-center justify-center">
                <i className="ri-key-2-line text-text-dark text-lg"></i>
              </div>
              <h2 className="text-text-dark text-lg font-heading font-semibold">序號列表</h2>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            {isLoading ? (
              // 載入中 UI
              <div className="p-12 text-center text-text-muted flex flex-col items-center gap-3">
                <i className="ri-loader-4-line text-3xl animate-spin"></i>
                <p>正在從金庫讀取你的序號...</p>
              </div>
            ) : error ? (
              // 錯誤提示 UI
              <div className="p-12 text-center text-accent-rose bg-accent-rose/5">
                <i className="ri-error-warning-line text-3xl mb-2 block"></i>
                <p>{error}</p>
              </div>
            ) : keys.length === 0 ? (
              // 空資料 UI
              <div className="p-12 text-center text-text-muted">
                <i className="ri-ghost-line text-4xl mb-3 block opacity-50"></i>
                <p>目前還沒有購買任何遊戲序號喔！</p>
              </div>
            ) : (
              // 真實資料表格
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="border-b border-fog-mid bg-fog/50">
                    <th className="text-left text-text-muted text-xs font-medium uppercase tracking-wider py-4 px-5 w-16">
                      項次
                    </th>
                    <th className="text-left text-text-muted text-xs font-medium uppercase tracking-wider py-4 px-5">
                      購買日期
                    </th>
                    <th className="text-left text-text-muted text-xs font-medium uppercase tracking-wider py-4 px-5">
                      遊戲名稱
                    </th>
                    <th className="text-left text-text-muted text-xs font-medium uppercase tracking-wider py-4 px-5">
                      序號
                    </th>
                    <th className="text-center text-text-muted text-xs font-medium uppercase tracking-wider py-4 px-5 w-20">
                      複製
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((entry: any, index) => {
                    // 💡 核心判斷：是否為等待發送中的預售商品
                    const isPreorderWaiting = 
                      entry.fulfillmentStatus === 'PREORDER_WAITING' || 
                      (!entry.key && entry.keyDeliveryDate);
                      
                    const displayDeliveryDate = formatLocalYYYYMMDD(entry.keyDeliveryDate);

                    return (
                      <tr key={entry.id} className="border-b border-fog-mid/50 hover:bg-fog/50 transition-colors">
                        <td className="py-4 px-5 text-text-dark text-sm">{index + 1}</td>
                        <td className="py-4 px-5 text-text-muted text-sm whitespace-nowrap">{entry.date}</td>
                        <td className="py-4 px-5">
                          <p className="text-text-dark text-sm font-medium">{entry.gameName}</p>
                          <p className="text-text-muted text-xs">{entry.gameNameEn}</p>
                        </td>
                        
                        {/* 💡 序號欄位：根據狀態切換顯示內容 */}
                        <td className="py-4 px-5">
                          {isPreorderWaiting ? (
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-accent-orange/10 border border-accent-orange/20">
                              <i className="ri-time-line text-accent-orange text-sm"></i>
                              <span className="text-accent-orange text-sm font-medium whitespace-nowrap">
                                預售中 {displayDeliveryDate ? `(${displayDeliveryDate} 發送)` : ''}
                              </span>
                            </div>
                          ) : (
                            <code className="text-accent-mint text-sm bg-fog px-3 py-1 rounded-md select-all whitespace-nowrap">
                              {entry.key}
                            </code>
                          )}
                        </td>
                        
                        {/* 💡 複製按鈕：預售狀態下不顯示複製按鈕 */}
                        <td className="py-4 px-5 text-center">
                          {isPreorderWaiting ? (
                            <span className="text-text-gray">-</span>
                          ) : (
                            <button
                              onClick={() => handleCopy(entry.key, entry.id)}
                              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-fog-mid transition-all duration-200 cursor-pointer mx-auto"
                              title="複製序號"
                            >
                              {copiedId === entry.id ? (
                                <i className="ri-check-line text-accent-mint text-lg"></i>
                              ) : (
                                <i className="ri-file-copy-line text-text-muted hover:text-text-dark text-lg"></i>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}